import { Visibility, VisibilityOff } from '@mui/icons-material'; // Import visibility icons
import { LoadingButton } from '@mui/lab';
import {
  Box,
  Checkbox,
  Divider,
  FormControlLabel,
  IconButton,
  InputAdornment,
  MenuItem,
  Select,
  Stack,
  TextField,
  Typography,
} from '@mui/material';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { StyledComponent } from 'src/components/styled-components/styledComponent';
import { useRightSidebar } from 'src/layouts/right-side-bar/rightSidebarProvider';
import HttpServices from 'src/services/httpService';
import { reportModuleURL } from 'src/services/urlService';
import { CommonHelper } from 'src/utils/commonHelper';
import { useToaster } from 'src/utils/toaster/toasterContext';

const SendAsEmailComponent = ({
  serviceName,
  startDate,
  endDate,
  viewBy,
  connectedProfileId,
  reportId,
  subReportId = 0,
  currReportData,
  isCustomReport,
}) => {
  const { t: translate } = useTranslation();
  const sideBarWidth = 500;
  const { showToast } = useToaster();
  let currentBrandId = CommonHelper.GetCurrentBrandId();
  const brandDetails = CommonHelper.GetBrandInfoFromLocalStorage('brand_details');
  const currentBrandDetails = brandDetails?.find((a) => a.brand_id === currentBrandId);
  const [email, setEmail] = useState('');
  const [subject, setSubject] = useState(
    `${translate('report.check_out_the')}  ${translate('report.account')} ${
     isCustomReport
          ? currReportData.custom_report_name
          : currReportData.report_display_name
          ? currReportData.report_display_name
          : currReportData.card_name} ${translate('report.for')} ${currentBrandDetails.brand_name}`
  );
  const [message, setMessage] = useState(
    `${translate('report.just_wanted_to_share')} ${
     isCustomReport
          ? currReportData.custom_report_name
          : currReportData.report_display_name
          ? currReportData.report_display_name
          : currReportData.card_name} ${translate('report.report_for')} ${currentBrandDetails.brand_name} ${translate('report.with_you')}. ${translate(
      'report.see_attachment_below'
    )}.`
  );
  const [format, setFormat] = useState('PDF');
  const [passwordProtect, setPasswordProtect] = useState(false);
  const [password, setPassword] = useState('');
  const [sendCopy, setSendCopy] = useState(false);
  const [isLoading, setLoading] = useState(false);
  const [emailError, setEmailError] = useState('');
  const [passwordError, setPasswordError] = useState('');
  const { handleCloseRightSidebar } = useRightSidebar();
  const [showPassword, setShowPassword] = useState(false);

  const handleSend = () => {
    setEmailError('');
    setPasswordError('');

    if (!email) {
      setEmailError(translate('report.email_address_is_required'));
      showToast(translate('report.email_address_is_required'), 'e');
      return;
    }

    const emailList = email.split(',').map((e) => e.trim());
    const isValidEmail = emailList.every((emailItem) => /^[\w-]+(\.[\w-]+)*@([\w-]+\.)+[a-zA-Z]{2,7}$/.test(emailItem));

    if (!isValidEmail) {
      setEmailError(translate('report.please_enter_valid_email'));
      showToast(translate('report.please_enter_valid_email'), 'e');
      return;
    }
    if (passwordProtect && password === '') {
      setPasswordError(translate('report.please_enter_the_password'));
      showToast(translate('report.please_enter_the_password'), 'e');
      return;
    }
    if (!subject || subject.trim() === '') {
      showToast(translate('report.subject_is_requuired'), 'e');
      return;
    }
    if (!message || message.trim() === '') {
      showToast(translate('report.message_is_required'), 'e');
      return;
    }

    setLoading(true);
    const formBody = {
      from: startDate,
      to: endDate,
      view_by: viewBy,
      type: format,
      report_id: reportId,
      sub_report_id: subReportId,
      service_name: serviceName,
      connected_profile_id: connectedProfileId,
      brand_id: currentBrandId,
      is_custom_report : isCustomReport
    };

    const emailbody = {
      recipient_email_addresses: emailList,
      subject: subject,
      message: message,
      report_format: format,
      is_password_protected: passwordProtect,
      password: passwordProtect ? password : '',
      send_me_a_copy: sendCopy,
    };

    const getProps = {
      url: reportModuleURL.sendAsEmailReport,
      body: {
        export_report: formBody,
        email_data: emailbody,
      },
      successCallback,
      failureCallback,
    };
    HttpServices.Post(getProps);
  };

  const successCallback = (data) => {
    showToast(`${translate('report.email_sent')}!`, 's');
    setLoading(false);
    handleCloseRightSidebar();
  };

  const failureCallback = (message) => {
    showToast(message, 'e');
    setLoading(false);
  };
  const handleClickShowPassword = () => {
    setShowPassword(!showPassword);
  };
  return (
    <StyledComponent.StyledStack sx={{ width: sideBarWidth, p: 3 }}>
      <Typography variant="h6" gutterBottom>
        {translate('report.email_report')}
      </Typography>
      <Stack spacing={2}>
        <TextField
          label={translate('report.recipient_email_address')}
          placeholder={translate('report.email_seperated_by_comma')}
          fullWidth
          required
          value={email}
          onChange={(e) => setEmail(e.target.value)}
          error={!!emailError}
          helperText={emailError}
        />

        <TextField
          label={translate('report.subject')}
          multiline
          rows={2}
          fullWidth
          required
          value={subject}
          onChange={(e) => setSubject(e.target.value)}
        />

        <TextField
          label={translate('report.message')}
          multiline
          rows={5}
          fullWidth
          value={message}
          onChange={(e) => setMessage(e.target.value)}
        />

        <Box>
          <Typography variant="body1" gutterBottom>
            {translate('report.report_format')}
          </Typography>
          <Select value={format} onChange={(e) => setFormat(e.target.value)} fullWidth>
            <MenuItem value="PDF">{translate('report.pdf')}</MenuItem>
            {/* <MenuItem value="PNG" disabled>PNG</MenuItem> */}
          </Select>
        </Box>

        <FormControlLabel
          control={<Checkbox checked={passwordProtect} onChange={(e) => setPasswordProtect(e.target.checked)} />}
          label={translate('report.password_protect')}
        />

        {passwordProtect && (
          <TextField
            label={translate('report.set_a_password')}
            placeholder={translate('report.password_should_be')}
            type={showPassword ? 'text' : 'password'} // Toggle between text and password
            fullWidth
            value={password}
            onChange={(e) => setPassword(e.target.value)}
            error={!!passwordError}
            helperText={passwordError}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <Typography variant="body2">*</Typography>
                </InputAdornment>
              ),
              endAdornment: (
                <InputAdornment position="end">
                  <IconButton aria-label="toggle password visibility" onClick={handleClickShowPassword} edge="end">
                    {showPassword ? <VisibilityOff /> : <Visibility />}
                  </IconButton>
                </InputAdornment>
              ),
            }}
          />
        )}

        {/* <FormControlLabel
          control={
            <Checkbox
              checked={sendCopy}
              onChange={(e) => setSendCopy(e.target.checked)}
            />
          }
          label="Send me a copy"
        /> */}

        <Divider />

        <Stack direction="row" spacing={2}>
          <LoadingButton variant="contained" color="primary" onClick={handleSend} loading={isLoading}>
            {translate('report.send')}
          </LoadingButton>
          {/* <Button color="inherit" onClick={() => console.log('Cancel clicked')}>Cancel</Button> */}
        </Stack>
      </Stack>
    </StyledComponent.StyledStack>
  );
};

export default SendAsEmailComponent;
